/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Hook for checking if current store has menus
 * Used to determine if store button should be enabled for new users
 * Now uses the menu search endpoint to count menus more efficiently
 *
 * @module hooks/menuScope/useStoreHasMenus
 *
 * @returns {Object} Store menus state
 * @returns {boolean} returns.hasMenus - Whether the current store has any menus
 * @returns {boolean} returns.isLoading - Whether menus are being loaded
 * @returns {boolean} returns.isError - Whether there was an error loading menus
 * @returns {number} returns.menusCount - Total number of menus in the store
 *
 * @example
 * const { hasMenus, isLoading, menusCount } = useStoreHasMenus();
 *
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 *
 * if (hasMenus) {
 *   // Enable store button
 * }
 */
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import useUserStore from 'hooks/useUserStore';
import { getMenuMakerMenusRequest } from 'utils/api/baas/menuMaker';
import { createHeaders } from 'utils/baasRequest';

interface UseStoreHasMenusReturn {
  /** Whether the current store has any menus */
  hasMenus: boolean;
  /** Whether menus are being loaded */
  isLoading: boolean;
  /** Whether there was an error loading menus */
  isError: boolean;
  /** Total number of menus in the store */
  menusCount: number;
}

export const useStoreHasMenus = (): UseStoreHasMenusReturn => {
  const { storeId } = useUserStore();

  // Use menu search endpoint to get total count of menus in the store
  // Using version 2 which calls /v1/menu-maker/menus/search/
  const { data, isFetching, isError } = useQuery(
    ['storeMenusCount', storeId],
    () =>
      getMenuMakerMenusRequest(
        {
          storeUuid: storeId,
          brand: '', // Empty brand to get menus from all brands in the store
          name: '',
          page: 1,
          pageSize: 1, // Only need count, not actual data
          version: 2, // Use version 2 for /menus/search/ endpoint
        },
        { headers: createHeaders() },
      ),
    {
      initialData: { data: [], pagination: { count: 0 } },
      enabled: !!storeId,
    },
  );

  // Calculate menus count from pagination response
  const { hasMenus, menusCount } = useMemo(() => {
    const totalCount = (data as any)?.pagination?.count || 0;

    return {
      hasMenus: totalCount > 0,
      menusCount: totalCount,
    };
  }, [(data as any)?.pagination?.count]);

  return {
    hasMenus,
    isLoading: isFetching,
    isError,
    menusCount,
  };
};
